<template>
  <v-container fluid class="ingredient-form-page">
    <div class="ingredient-form-header">
      <v-btn text class="text-none back-btn" @click="goBack">
        <v-icon left small>fal fa-arrow-left</v-icon>
        {{ $t("back") }}
      </v-btn>
      <div class="ingredient-form-title">
        {{ isEdit ? $t("update") : $t("new_data") }} {{ $t("ingredient") }}
      </div>
    </div>

    <v-row v-if="loadingPage">
      <v-col cols="12" lg="6">
        <v-card outlined class="rounded-xl ingredient-form-card">
          <v-card-text class="pa-5 pa-md-6">
            <div class="skeleton-block skeleton-label"></div>
            <div class="ingredient-form-skeleton-chips mt-4">
              <v-skeleton-loader
                v-for="index in 3"
                :key="`language-skeleton-${index}`"
                type="chip"
                width="72"
              />
            </div>
            <div
              v-for="index in 4"
              :key="`detail-field-skeleton-${index}`"
              class="skeleton-block skeleton-field mt-6"
            ></div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" lg="6">
        <v-card outlined class="rounded-xl ingredient-form-card">
          <v-card-text class="pa-5 pa-md-6">
            <div class="skeleton-block skeleton-label"></div>
            <div class="ingredient-form-skeleton-unit mt-5">
              <div class="skeleton-block skeleton-field" style="flex: 1"></div>
              <div class="skeleton-block skeleton-field" style="width: 170px"></div>
            </div>
            <div
              v-for="index in 3"
              :key="`stock-field-skeleton-${index}`"
              class="skeleton-block skeleton-field mt-6"
            ></div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-else>
      <v-col cols="12" lg="6">
        <v-card outlined class="rounded-xl ingredient-form-card left-card">
          <v-card-text class="pa-5 pa-md-6">
            <div class="section-label mb-0">{{ $t("ingredient") }}</div>

            <div class="language-toggle-row">
              <span class="language-toggle-label">{{ $t("ui_add_language") }}:</span>
              <div class="language-toggle-group">
                <button
                  type="button"
                  class="language-toggle-btn"
                  :class="{ active: showEn }"
                  @click="showEn = !showEn"
                >
                  <LanguageFlag code="en" /> EN
                </button>
                <button
                  type="button"
                  class="language-toggle-btn"
                  :class="{ active: showTh }"
                  @click="showTh = !showTh"
                >
                  <LanguageFlag code="th" /> TH
                </button>
                <button
                  type="button"
                  class="language-toggle-btn"
                  :class="{ active: showCn }"
                  @click="showCn = !showCn"
                >
                  <LanguageFlag code="ch" /> CN
                </button>
              </div>
            </div>

            <div class="section-block">
              <TTextField
                class="language-name-field"
                skip-translation
                flag-code="la"
                :label="$t('product_name')"
                v-model="form.name_la"
                required
                :counter="true"
                :maxlength="200"
                :errorMessages="nameLaErrors"
              />
              <TTextField
                v-if="showEn"
                skip-translation
                class="language-name-field mt-4"
                flag-code="en"
                :label="$t('name_en')"
                v-model="form.name_en"
                :counter="true"
                :maxlength="200"
                :errorMessages="nameEnErrors"
                @input="$v.form.name_en.$touch()"
                @blur="$v.form.name_en.$touch()"
              />
              <TTextField
                v-if="showTh"
                skip-translation
                class="language-name-field mt-4"
                flag-code="th"
                :label="$t('name_th')"
                v-model="form.name_th"
                :counter="true"
                :maxlength="200"
                :errorMessages="nameThErrors"
                @input="$v.form.name_th.$touch()"
                @blur="$v.form.name_th.$touch()"
              />
              <TTextField
                v-if="showCn"
                skip-translation
                class="language-name-field mt-4"
                flag-code="ch"
                :label="$t('name_cn')"
                v-model="form.name_cn"
                :counter="true"
                :maxlength="200"
                :errorMessages="nameCnErrors"
                @input="$v.form.name_cn.$touch()"
                @blur="$v.form.name_cn.$touch()"
              />
            </div>

            <div class="section-block">
              <div class="section-label">{{ $t("code") }}</div>
              <TTextField
                class="ingredient-barcode-field"
                label=""
                v-model="form.code"
                :counter="true"
                :maxlength="20"
                :errorMessages="codeErrors"
                @input="$v.form.code.$touch()"
                @blur="$v.form.code.$touch()"
              >
                <template v-slot:append>
                  <v-btn
                    icon
                    class="ingredient-barcode-generate-btn"
                    @click="generateBarcode"
                    :title="$t('generate_barcode')"
                  >
                    <v-icon size="18">fal fa-barcode-scan</v-icon>
                  </v-btn>
                </template>
              </TTextField>
            </div>

            <div class="section-block">
              <div class="section-label">{{ $t("supplier") }}</div>
              <TextSelect
                :placeholder="false"
                searchable
                label="supplier"
                itemValue="id"
                text="name"
                :returnObject="false"
                :items="suppliers"
                v-model="form.vendor_id"
              />
            </div>

            <div class="enable-switch-row enable-switch-row-bottom">
              <span class="enable-switch-label">{{ $t("active") }}</span>
              <v-switch
                v-model="form.is_active"
                inset
                hide-details
                class="enable-switch-control"
              />
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" lg="6">
        <v-card outlined class="rounded-xl ingredient-form-card right-card">
          <v-card-text class="pa-5 pa-md-6">
            <div class="section-label">{{ $t("stock") }}</div>

            <div class="unit-row">
              <TextSelect
                class="unit-row-select"
                required
                searchable
                label="unit"
                itemValue="id"
                text="label"
                :returnObject="false"
                :items="unitOptions"
                v-model="form.unit_id"
                :errorMessages="unitErrors"
                @input="$v.form.unit_id.$touch()"
                @blur="$v.form.unit_id.$touch()"
              />
              <v-btn
                depressed
                outlined
                color="primary"
                class="rounded-pill text-none unit-row-btn"
                :disabled="!form.unit_id"
                @click="conversionDialog = true"
              >
                <v-icon left small>fal fa-exchange</v-icon>
                {{ $t("conversion_rates") }}
              </v-btn>
            </div>

            <div class="section-block">
              <div class="section-label">{{ currentStockLabel }}</div>
              <CurrencyInput
                label=""
                skipTranslation
                v-model="form.current_stock"
                :disabled="isEdit"
                :errorMessages="currentStockErrors"
                @input="$v.form.current_stock.$touch()"
                @blur="$v.form.current_stock.$touch()"
              />
            </div>

            <div class="form-grid section-block">
              <div>
                <div class="section-label">{{ minimumStockLabel }}</div>
                <CurrencyInput
                  label=""
                  skipTranslation
                  v-model="form.minimum_stock"
                  :errorMessages="minimumStockErrors"
                  @input="$v.form.minimum_stock.$touch()"
                  @blur="$v.form.minimum_stock.$touch()"
                />
              </div>
              <div>
                <div class="section-label">{{ unitCostLabel }}</div>
                <CurrencyInput
                  label=""
                  skipTranslation
                  v-model="form.unit_cost"
                  :errorMessages="unitCostErrors"
                  @input="$v.form.unit_cost.$touch()"
                  @blur="$v.form.unit_cost.$touch()"
                />
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <div v-if="!loadingPage" class="ingredient-form-actions">
      <v-btn
        depressed
        color="#DDEAFD"
        class="text-none primary--text rounded-pill form-action-btn"
        @click="goBack"
      >
        {{ $t("cancel") }}
      </v-btn>
      <v-spacer />
      <v-btn
        depressed
        color="primary"
        class="text-none rounded-pill form-action-btn form-action-btn-primary"
        :loading="saving"
        :disabled="saving"
        @click="submitForm"
      >
        <v-icon left small>fal fa-save</v-icon>
        {{ isEdit ? $t("update") : $t("save_inventory") }}
      </v-btn>
    </div>

    <ConversionRatesModal
      v-model="conversionDialog"
      :units="unitOptions"
      :base-unit-id="form.unit_id"
      :conversions="form.unit_conversions"
      @save="onConversionsSaved"
    />
  </v-container>
</template>

<script>
import TextSelect from "@/components/global/TextSelect";
import TTextField from "@/components/global/TextField";
import CurrencyInput from "@/components/CurrencyInput";
import ConversionRatesModal from "@/components/manage/ConversionRatesModal";
import { success } from "@/controllers/alert";
import { required, maxLength } from "vuelidate/lib/validators";
import LanguageFlag from "@/components/global/LanguageFlag.vue";

const optionalNonNegative = (value) => {
  if (value === null || value === undefined || value === "") return true;
  return Number(value) >= 0;
};

export default {
  name: "IngredientFormPage",
  components: {
    TextSelect,
    TTextField,
    CurrencyInput,
    ConversionRatesModal,
    LanguageFlag,
  },

  data: () => ({
    loadingPage: true,
    saving: false,
    conversionDialog: false,
    suppliers: [],
    units: [],
    showEn: false,
    showTh: false,
    showCn: false,
    form: {
      id: null,
      vendor_id: null,
      code: "",
      name_la: "",
      name_en: "",
      name_th: "",
      name_cn: "",
      unit_id: null,
      current_stock: 0,
      minimum_stock: 0,
      unit_cost: 0,
      is_active: true,
      unit_conversions: [],
    },
  }),
  validations: {
    form: {
      code: { maxLength: maxLength(50) },
      name_la: { required, maxLength: maxLength(200) },
      name_en: { maxLength: maxLength(200) },
      name_th: { maxLength: maxLength(200) },
      name_cn: { maxLength: maxLength(200) },
      unit_id: { required },
      current_stock: { optionalNonNegative },
      minimum_stock: { optionalNonNegative },
      unit_cost: { optionalNonNegative },
    },
  },
  computed: {
    ingredientId() {
      return this.$route.params.id;
    },
    isEdit() {
      return Boolean(this.ingredientId);
    },
    pageTitle() {
      return this.isEdit ? this.$t("update") : this.$t("new_data");
    },
    unitOptions() {
      return this.units.map((unit) => ({
        ...unit,
        label: this.unitLabel(unit),
      }));
    },
    selectedUnitLabel() {
      const selectedUnit = this.unitOptions.find(
        (unit) => String(unit.id) === String(this.form.unit_id)
      );
      return selectedUnit?.label === "-" ? "" : selectedUnit?.label || "";
    },
    currentStockLabel() {
      return this.labelWithUnit("current_stock");
    },
    minimumStockLabel() {
      return this.labelWithUnit("minimum_stock");
    },
    unitCostLabel() {
      return this.labelWithUnit("unit_cost");
    },
    codeErrors() {
      return this.maxLengthErrors("code", "code", 50);
    },
    nameLaErrors() {
      return [
        ...this.requiredErrors("name_la"),
        ...this.maxLengthErrors("name_la", "name_la", 200),
      ];
    },
    nameEnErrors() {
      return this.maxLengthErrors("name_en", "name_en", 200);
    },
    nameThErrors() {
      return this.maxLengthErrors("name_th", "name_th", 200);
    },
    nameCnErrors() {
      return this.maxLengthErrors("name_cn", "name_cn", 200);
    },
    unitErrors() {
      return this.requiredErrors("unit_id");
    },
    currentStockErrors() {
      return this.nonNegativeErrors("current_stock");
    },
    minimumStockErrors() {
      return this.nonNegativeErrors("minimum_stock");
    },
    unitCostErrors() {
      return this.nonNegativeErrors("unit_cost");
    },
  },
  async created() {
    try {
      await Promise.all([this.fetchSuppliers(), this.fetchUnits()]);
      if (this.isEdit) {
        await this.fetchIngredient();
      }
    } finally {
      this.loadingPage = false;
    }
  },
  methods: {
    async fetchIngredient() {
      const { data } = await this.$http.get(`ingredients/${this.ingredientId}`);
      if (data.status) {
        this.form = this.mapIngredientToForm(data.data);
        this.showEn = Boolean(this.form.name_en);
        this.showTh = Boolean(this.form.name_th);
        this.showCn = Boolean(this.form.name_cn);
      }
    },
    async fetchSuppliers() {
      const { data } = await this.$http.get("suppliers");
      if (data.status) {
        this.suppliers = Array.isArray(data.data) ? data.data : [];
      }
    },
    async fetchUnits() {
      const { data } = await this.$http.get("product_size");
      if (data.status) {
        this.units = Array.isArray(data.data) ? data.data : [];
      }
    },
    async submitForm() {
      this.$v.form.$touch();
      if (this.$v.form.$invalid) return;

      try {
        this.saving = true;
        const payload = this.buildPayload();
        const { data } = this.isEdit
          ? await this.$http.put(`ingredients/${this.ingredientId}`, payload)
          : await this.$http.post("ingredients", payload);
        if (data.status) {
          await success(this, data.message);
          this.goBack();
        }
      } catch (error) {
        console.log(error);
      } finally {
        this.saving = false;
      }
    },
    buildPayload() {
      const payload = {
        vendor_id: this.form.vendor_id || null,
        code: this.form.code || null,
        name_la: this.form.name_la,
        name_en: this.form.name_en || null,
        name_th: this.form.name_th || null,
        name_cn: this.form.name_cn || null,
        unit_id: this.form.unit_id,
        minimum_stock: this.numberOrZero(this.form.minimum_stock),
        unit_cost: this.numberOrZero(this.form.unit_cost),
        is_active: this.form.is_active ? 1 : 0,
        unit_conversions: this.form.unit_conversions,
      };

      if (!this.isEdit) {
        payload.current_stock = this.numberOrZero(this.form.current_stock);
      }

      return payload;
    },
    mapIngredientToForm(item = {}) {
      return {
        id: item.id || null,
        vendor_id: item.vendor_id || null,
        code: item.code || "",
        name_la: item.name_la || "",
        name_en: item.name_en || "",
        name_th: item.name_th || "",
        name_cn: item.name_cn || "",
        unit_id: item.unit_id || null,
        current_stock: this.numberOrZero(item.current_stock),
        minimum_stock: this.numberOrZero(item.minimum_stock),
        unit_cost: this.numberOrZero(item.unit_cost),
        is_active: Number(item.is_active) === 1,
        unit_conversions: Array.isArray(item.unit_conversions)
          ? item.unit_conversions
          : [],
      };
    },
    onConversionsSaved(rows) {
      this.form.unit_conversions = rows;
    },
    unitLabel(unit) {
      if (!unit) return "-";
      const localizedField = { en: "name_en", th: "name_th", ch: "name_cn" }[
        this.$i18n.locale
      ];
      const name = (localizedField && unit[localizedField]) || unit.name;
      return name || "-";
    },
    labelWithUnit(labelKey) {
      const label = this.$t(labelKey);
      return this.selectedUnitLabel
        ? `${label} (${this.selectedUnitLabel})`
        : label;
    },
    numberOrZero(value) {
      if (value === null || value === undefined || value === "") return 0;
      return Number(value);
    },
    requiredErrors(field) {
      const errors = [];
      if (!this.$v.form[field].$dirty) return errors;
      if (!this.$v.form[field].required) {
        errors.push(this.$t("validation_required"));
      }
      return errors;
    },
    maxLengthErrors(field, labelKey, max) {
      const errors = [];
      if (!this.$v.form[field].$dirty) return errors;
      if (!this.$v.form[field].maxLength) {
        errors.push(
          this.$t("validation_max_length_dynamic", {
            field: this.$t(labelKey),
            max,
          })
        );
      }
      return errors;
    },
    nonNegativeErrors(field) {
      const errors = [];
      if (!this.$v.form[field].$dirty) return errors;
      if (!this.$v.form[field].optionalNonNegative) {
        errors.push(this.$t("validation_min_zero"));
      }
      return errors;
    },
    generateBarcode() {
      const today = new Date().toISOString().substr(0, 8).replaceAll("-", "");
      const randomDigits = (crypto.getRandomValues(new Uint32Array(1))[0] % 1000)
        .toString()
        .padStart(3, "0");

      this.form.code = `${today}${randomDigits}`;
    },
    goBack() {
      this.$router.push("/manage-ingredients");
    },
  },
};
</script>

<style scoped>
.ingredient-form-page {
  padding-bottom: 32px;
}

/* ---- header ---- */
.ingredient-form-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 20px;
}

.back-btn {
  margin-left: -12px;
  color: #475569;
}

.ingredient-form-title {
  font-size: 28px;
  font-weight: 700;
  color: #0f172a;
}

/* ---- skeleton ---- */
.skeleton-block {
  border-radius: 8px;
  background: linear-gradient(90deg, #eef1f5 25%, #f7f9fb 37%, #eef1f5 63%);
  background-size: 400% 100%;
  animation: ingredient-skeleton-shimmer 1.4s ease infinite;
}

@keyframes ingredient-skeleton-shimmer {
  0% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
}

.skeleton-label {
  width: 120px;
  height: 14px;
  margin-bottom: 12px;
}

.skeleton-field {
  width: 100%;
  height: 46px;
  border-radius: 10px;
}

.ingredient-form-skeleton-chips,
.ingredient-form-skeleton-unit {
  align-items: center;
  display: flex;
  gap: 12px;
}

.ingredient-form-skeleton-chips {
  flex-wrap: wrap;
}

.ingredient-form-skeleton-unit {
  align-items: flex-start;
}

/* ---- cards ---- */
.ingredient-form-card {
  border-color: #e8eef6;
}

.left-card,
.right-card {
  min-height: 100%;
}

.section-label {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 10px;
}

.section-block {
  margin-top: 24px;
}

.enable-switch-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.enable-switch-row-bottom {
  justify-content: space-between;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #eef1f5;
}

.enable-switch-label {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}

.enable-switch-control {
  margin: 0;
  flex: none;
}

/* Light gray filled inputs, matching the promotion form reference design */
.ingredient-form-card ::v-deep .v-text-field--outlined .v-input__slot {
  background: #f1f5f9 !important;
  border-radius: 10px !important;
  min-height: 46px;
}

.ingredient-form-card ::v-deep .v-text-field--outlined fieldset {
  border-color: transparent !important;
  transition: border-color 0.2s ease;
}

.ingredient-form-card ::v-deep .v-text-field--outlined.v-input--is-focused fieldset {
  border-color: #438ef2 !important;
  border-width: 1.5px !important;
}

/* Center the select dropdown arrow / clear icon inside the 46px-tall slot */
.ingredient-form-card ::v-deep .v-input__append-inner {
  align-self: center !important;
  margin-top: 0 !important;
}

/* Error/counter text sat flush against the input box's bottom edge —
   give it some breathing room, plus the same left inset as the input's
   own text. */
.ingredient-form-card ::v-deep .v-text-field__details {
  padding-left: 14px !important;
  margin-top: 4px !important;
}

.ingredient-form-card ::v-deep .v-messages__message {
  line-height: 1.4;
}

/* ---- language toggle ---- */
.language-toggle-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 18px;
}

.language-toggle-label {
  font-size: 12px;
  font-weight: 600;
  color: #94a3b8;
}

.language-toggle-group {
  display: inline-flex;
  padding: 4px;
  border-radius: 999px;
  background: #f1f5f9;
  gap: 4px;
}

.language-toggle-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: none;
  background: transparent;
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
}

.language-toggle-btn.active {
  /* #438ef2 fails WCAG contrast with white text at this font size;
     #1d5fc2 (already used for the promotion form's active day-pill)
     keeps the brand hue while passing AA (~6:1). */
  background: #1d5fc2;
  color: #fff;
}

/* ---- barcode field ---- */
.ingredient-barcode-field::v-deep .v-input__append-inner {
  align-self: center !important;
  display: flex !important;
  height: 40px !important;
  margin-top: 0 !important;
}

.ingredient-barcode-generate-btn {
  align-items: center !important;
  display: inline-flex !important;
  height: 34px !important;
  justify-content: center !important;
  margin: 0 !important;
  min-width: 34px !important;
  width: 34px !important;
}

/* ---- unit row ---- */
.unit-row {
  align-items: center;
  display: flex;
  gap: 12px;
}

.unit-row-select {
  flex: 1;
  min-width: 0;
}

.unit-row-btn {
  flex-shrink: 0;
  height: 46px !important;
  padding: 0 20px !important;
  font-weight: 600;
  background: #fff !important;
  /* Lines the button up with the select's INPUT box (not the wrapper's
     bottom edge, which can grow taller when a validation error message
     appears below the select) — matches the label row's own height. */
  margin-top: 24px;
  transition: background 0.2s ease, box-shadow 0.2s ease;
}

.unit-row-btn:hover {
  background: #eef6ff !important;
  box-shadow: 0 2px 8px rgba(67, 142, 242, 0.18);
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
}

@media (max-width: 600px) {
  .unit-row {
    flex-wrap: wrap;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }
}

/* ---- actions ---- */
.ingredient-form-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
  padding: 0 12px;
}

.form-action-btn {
  height: 44px !important;
  padding: 0 22px !important;
  font-weight: 700;
  border-radius: 999px !important;
  box-shadow: none !important;
  letter-spacing: normal;
}

.form-action-btn >>> .v-btn__content {
  font-size: 14px;
}

.form-action-btn-primary {
  height: 52px !important;
  padding: 0 34px !important;
}

.form-action-btn-primary >>> .v-btn__content {
  font-size: 16px;
}

@media (max-width: 960px) {
  .ingredient-form-header {
    flex-direction: column;
  }
}

@media (max-width: 600px) {
  .ingredient-form-title {
    font-size: 24px;
  }

  .ingredient-form-actions {
    flex-wrap: wrap;
    padding: 0;
  }
}
</style>
